import {
  Chip,
  ChipOwnProps,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ProprAccountOption,
  ProprAccountStatus,
  TERMINAL_PROPR_STATUSES,
} from "../api/useAPI";

/** A status an account can never come back from — never selectable. */
export const isTerminalProprStatus = (status: ProprAccountStatus) =>
  TERMINAL_PROPR_STATUSES.includes(status);

export const proprStatusColor = (
  status: ProprAccountStatus,
): ChipOwnProps["color"] => {
  switch (status) {
    case "active":
      return "success";
    case "passed":
      return "info";
    case "review_pending":
      return "warning";
    case "failed":
    case "closed":
      return "error";
    default:
      return "default";
  }
};

const amount = (value: string, currency: string) => {
  const num = parseFloat(value);
  if (Number.isNaN(num)) {
    return value;
  }
  return `${num.toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })} ${currency}`;
};

const shortDate = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
};

interface Props {
  options: ProprAccountOption[];
  selected: string;
  onSelect: (accountId: string) => void;
  /**
   * The account currently stored for this bot, if any. It stays selectable even
   * when terminal, so an in-modal choice can be reverted — a challenge can fail
   * (or a funded account be closed) while the bot is running. Omit when creating
   * a new account: then nothing terminal is ever selectable.
   */
  currentAccountId?: string;
}

/**
 * The Propr account table, shared by the settings modal and the add-profile
 * form. It owns the selectability rules so the two screens cannot drift apart:
 * failed / closed accounts cannot be picked, and funded accounts are flagged as
 * real money.
 */
export default function ProprAccountPicker({
  options,
  selected,
  onSelect,
  currentAccountId,
}: Props) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>Status</TableCell>
            <TableCell>Account ID</TableCell>
            <TableCell>Challenge</TableCell>
            <TableCell align="right">Size</TableCell>
            <TableCell align="right">Balance</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Created</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {options.map((option) => {
            const isCurrent =
              !!currentAccountId && option.accountId === currentAccountId;
            const dead = isTerminalProprStatus(option.status);
            const selectable = !dead || isCurrent;
            const isFunded = option.source === "funded";

            return (
              <TableRow
                key={option.accountId}
                hover={selectable}
                selected={option.accountId === selected}
                onClick={
                  selectable ? () => onSelect(option.accountId) : undefined
                }
                sx={{
                  cursor: selectable ? "pointer" : "not-allowed",
                  opacity: selectable ? 1 : 0.5,
                }}
              >
                <TableCell padding="checkbox">
                  <Radio
                    checked={option.accountId === selected}
                    disabled={!selectable}
                    value={option.accountId}
                    onChange={() => onSelect(option.accountId)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={proprStatusColor(option.status)}
                    variant={dead ? "filled" : "outlined"}
                    label={option.status}
                  />
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>
                  {option.accountId}
                  {isCurrent && (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label="current"
                      sx={{ ml: 1 }}
                    />
                  )}
                  {option.isBeta && (
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label="beta"
                      sx={{ ml: 1 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {option.slug}
                  {isFunded && (
                    <Chip
                      size="small"
                      color="error"
                      variant="filled"
                      label="real money"
                      sx={{ ml: 1 }}
                    />
                  )}
                </TableCell>
                <TableCell align="right">
                  {amount(option.size, option.currency)}
                </TableCell>
                <TableCell align="right">
                  {amount(option.balance, option.currency)}
                </TableCell>
                <TableCell>{option.accountType}</TableCell>
                <TableCell>{shortDate(option.createdAt)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
